import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import {
  type AdminFailedConsultation,
  listFailedConsultations,
  retryFailedConsultation,
} from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'

export const Route = createFileRoute('/admin/processing')({
  component: AdminProcessingPage,
})

function AdminProcessingPage() {
  const [days, setDays] = useState<number>(14)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['admin', 'processing', 'failed', days],
    queryFn: () => listFailedConsultations({ days, limit: 100 }),
  })
  const queryClient = useQueryClient()

  const retryMutation = useMutation({
    mutationFn: retryFailedConsultation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'processing', 'failed'] })
    },
  })

  return (
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        to="/admin"
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />В админ-панель
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Упавшие консультации
          </h1>
          <p className="text-sm text-muted-foreground">
            {query.data
              ? `${query.data.length} записей за последние ${days} дн.`
              : 'Загружаем список…'}
          </p>
        </div>
        <PeriodToggle value={days} onChange={setDays} />
      </header>

      {retryMutation.isError && <RetryErrorBanner error={retryMutation.error} />}

      {query.isLoading ? (
        <StatusCard title="Загружаем упавшие консультации" />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : query.data && query.data.length === 0 ? (
        <StatusCard
          title="Упавших консультаций нет"
          description="За выбранный период ничего не сломалось — отлично."
        />
      ) : (
        <FailedTable
          rows={query.data ?? []}
          expandedId={expandedId}
          onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
          onRetry={(id) => retryMutation.mutate(id)}
          retryingId={retryMutation.variables ?? null}
          retryPending={retryMutation.isPending}
        />
      )}
    </main>
  )
}

interface PeriodToggleProps {
  value: number
  onChange: (days: number) => void
}

const DAYS_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1 день' },
  { value: 7, label: '7 дней' },
  { value: 14, label: '14 дней' },
  { value: 30, label: '30 дней' },
]

function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  return (
    <div className="inline-flex rounded-md border bg-card p-1">
      {DAYS_OPTIONS.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`rounded-sm px-3 py-1 text-sm transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

interface FailedTableProps {
  rows: AdminFailedConsultation[]
  expandedId: string | null
  onToggleExpand: (id: string) => void
  onRetry: (id: string) => void
  retryingId: string | null
  retryPending: boolean
}

function FailedTable({
  rows,
  expandedId,
  onToggleExpand,
  onRetry,
  retryingId,
  retryPending,
}: FailedTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <th className="w-8 px-2 py-3 font-medium" />
            <th className="px-4 py-3 font-medium">Продавец</th>
            <th className="px-4 py-3 font-medium">Магазин</th>
            <th className="px-4 py-3 font-medium">Дата</th>
            <th className="px-4 py-3 font-medium">Обновлено</th>
            <th className="px-4 py-3 font-medium">Код</th>
            <th className="px-4 py-3 text-right font-medium">Действия</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expanded = expandedId === row.id
            const isRetrying = retryPending && retryingId === row.id
            return (
              <>
                <tr key={row.id} className="border-t">
                  <td className="px-2 py-3 align-top">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label={expanded ? 'Свернуть' : 'Раскрыть'}
                      onClick={() => onToggleExpand(row.id)}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </td>
                  <td className="px-4 py-3 font-medium">{row.sellerName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.storeName}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatConsultationDate(row.consultationDate)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatRelativeDate(row.updatedAt)}
                  </td>
                  <td className="px-4 py-3">
                    {row.errorCode ? (
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {row.errorCode}
                      </code>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRetry(row.id)}
                        disabled={isRetrying}
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`}
                          aria-hidden="true"
                        />
                        {isRetrying ? 'Запускаем…' : 'Повторить'}
                      </Button>
                    </div>
                  </td>
                </tr>
                {expanded && (
                  <tr key={`${row.id}-details`} className="border-t bg-muted/30">
                    <td className="px-2 py-3" />
                    <td colSpan={6} className="px-4 py-3">
                      <ExpandedDetails row={row} />
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ExpandedDetails({ row }: { row: AdminFailedConsultation }) {
  return (
    <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
      <DetailRow label="Код ошибки" value={row.errorCode ?? '—'} mono />
      <DetailRow
        label="Длительность"
        value={row.audioDurationSec ? `${Math.round(row.audioDurationSec)} с` : '—'}
      />
      <DetailRow
        label="Сообщение"
        value={row.errorMessage ?? 'Сообщение об ошибке не сохранено'}
        colSpan={2}
      />
    </dl>
  )
}

interface DetailRowProps {
  label: string
  value: string
  mono?: boolean
  colSpan?: number
}

function DetailRow({ label, value, mono, colSpan }: DetailRowProps) {
  return (
    <div className={colSpan === 2 ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-1 break-words ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  )
}

function formatConsultationDate(iso: string): string {
  // iso приходит в формате YYYY-MM-DD от бэкенда.
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) {
    return iso
  }
  return `${d}.${m}.${y}`
}

function formatRelativeDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function RetryErrorBanner({ error }: { error: unknown }) {
  const message = describeErrorMessage(error, 'Не удалось отправить на повтор.')
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

function StatusCard({ title, description }: { title: string; description?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent className="sr-only">{description}</CardContent>
    </Card>
  )
}
